import type { SxProps, Theme } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { ConditionConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  StatementDataColumnConfiguration,
  StatementLineConfiguration,
} from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import {
  selectFinancialStatementConfiguration,
  selectSelectedStatementLineId,
  selectStatementLines,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectFieldCaptionLookup } from "../../../../store/selectors/metaDataSelectors.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import { createFinancialStatementDefaultSettings } from "../../../../utilities/isConfigurationValid.ts";
import BiErrorCodeComponent from "../../../viewer/common/bi-error/BiErrorCodeComponent.tsx";
import PartContainer from "../../../viewer/common/PartContainer.tsx";
import PreviewActions from "../../../viewer/common/PreviewActions.tsx";
import PreviewComponentAlert from "../../../viewer/common/PreviewComponentAlert.tsx";
import ValidationState from "../../../viewer/common/ValidationState.tsx";
import FinancialStatementGrid from "../../../viewer/financialStatement/FinancialStatementGrid.tsx";
import BuildProgressWrapper from "../../BuildProgressWrapper.tsx";
import ChangePreviewModeButton from "../../common/ChangePreviewModeButton.tsx";
import { getStatementLineLabel } from "../utils/statementRowsArea.ts";
import useDataLoadingBuilder from "./useDataLoadingBuilder.ts";

const noLines: StatementLineConfiguration[] = [];
const noDataColumns: StatementDataColumnConfiguration[] = [];
const noConditions: ConditionConfiguration[] = [];
const defaultSettings = createFinancialStatementDefaultSettings();
const previewSx: SxProps<Theme> = { borderRight: "unset", p: "1.3rem", pt: 2 };
const previewContainerSx: SxProps<Theme> = { position: "relative", gap: "1rem" };

const FinancialStatementPreview = () => {
  const dispatch = useAppDispatch();
  const { onRefreshGrid } = useBiCommonDataContext();
  const configuration = useSelector(selectFinancialStatementConfiguration);
  const selectedLineId = useSelector(selectSelectedStatementLineId);
  const getFieldCaption = useSelector(selectFieldCaptionLookup);
  const lines = useSelector(selectStatementLines);
  const { statement, calculating, error, failedMeasures, configurationValidity } = useDataLoadingBuilder();

  const validationFields = useMemo(
    () =>
      lines.flatMap((line) =>
        line.field ? [{ config: line.field.config, meta: { caption: getStatementLineLabel(line, getFieldCaption) } }] : []
      ),
    [getFieldCaption, lines]
  );

  const handleLineClick = useCallback(
    (lineId: string) => {
      dispatch(createFinancialStatementAction("setSelectedLineId", lineId));
    },
    [dispatch]
  );

  return (
    <BuildProgressWrapper>
      <PartContainer
        sx={previewSx}
        containerSx={previewContainerSx}
        caption="Preview"
        actions={<PreviewActions onRefresh={onRefreshGrid} ChangePreviewModeButton={<ChangePreviewModeButton />} />}
      >
        <BiErrorCodeComponent errorMessage={error}>
          <PreviewComponentAlert error={error} failedMeasures={failedMeasures} />
          <ValidationState state={configurationValidity} fields={validationFields}>
            <FinancialStatementGrid
              response={configurationValidity.valid && !error ? statement : undefined}
              lines={configuration?.lines ?? noLines}
              dataColumns={configuration?.columns.dataColumns ?? noDataColumns}
              conditions={configuration?.conditions ?? noConditions}
              settings={configuration?.settings ?? defaultSettings}
              getFieldCaption={getFieldCaption}
              loading={calculating}
              selectedLineId={selectedLineId}
              onLineClick={handleLineClick}
            />
          </ValidationState>
        </BiErrorCodeComponent>
      </PartContainer>
    </BuildProgressWrapper>
  );
};

export default FinancialStatementPreview;
